import { useTranslation } from 'react-i18next'
import {
  AuftragOption,
  AuftragOptionen,
  BefehlFormAuftrag,
  BefehlFormField
} from '../Generic'
import {
  ControllerRenderProps,
  FieldErrors,
  useFormContext
} from 'react-hook-form'
import { Input, RadioChipGroup, RadioChipOption } from '../../form'
import { useEffect, useMemo } from 'react'

export interface Auftrag33_10FormProps {
  fieldPrefix: string
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings?: boolean
  errors?: FieldErrors
}

export default function Auftrag33_10Form({
  readOnly,
  fieldPrefix,
  'data-testid': testId,
  errors,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag33_10FormProps>) {
  const formContext = useFormContext()
  const { control, formState, watch, setValue } = formContext

  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const { t } = useTranslation('ui-befehle')

  const selectionType = watch(`${fieldPrefix}.selector`)
  const auftragActive = watch(`${fieldPrefix}.enabled`)

  useEffect(() => {
    if (selectionType === 'lzbBk') {
      setValue('auftrag_33_10.lzb_etcs', 'lzb_fahrt')
    } else if (selectionType === 'etcsBk') {
      setValue('auftrag_33_10.lzb_etcs', 'etcs_fahrterlaubnis')
    }
    if (!auftragActive) {
      setValue('auftrag_33_10.selector', '')
    }
  }, [selectionType, auftragActive, setValue])

  return (
    <BefehlFormAuftrag
      auftragKennung="33.10"
      fieldName="auftrag33_10.enabled"
      readOnly={true}
      disabled={false}
      testId={testId}
    >
      <span data-testid={`${testId}-text`}>{t('auftraege.33_10.prefix')}</span>
      <div className="flex flex-col flex-wrap items-baseline sm:flex-row w-full gap-0.5">
        <BefehlFormField
          name={`${fieldPrefix}.selector`}
          control={control}
          label=""
          readOnly={readOnly}
          activateEmptyWarning={activateEmptyFieldWarnings}
          useTextDisplayInReadOnly={false}
          data-testid={`${testId}-feld-33-11-12-selector`}
          className="w-full max-w-xl"
          render={({ field }: { field: ControllerRenderProps }) => (
            <AuftragOptionen
              name={`${fieldPrefix}.selector`}
              value={field.value}
              onChange={field.onChange}
              readOnly={readOnly}
              errors={fieldErrors}
              data-testid={`${testId}-feld-33-11-12-selector-options`}
            >
              <AuftragOption
                value={'lzbBk'}
                label={t('auftraege.33_10.lzbBk_label')}
                data-testid={`${testId}-feld-33-11-12-selector-options-lzb-bk`}
              >
                <BefehlFormField
                  name={`${fieldPrefix}.feld_33_11`}
                  label={t('auftraege.33_10.lzbBk')}
                  control={control}
                  readOnly={readOnly}
                  activateEmptyWarning={activateEmptyFieldWarnings}
                  data-testid={`${testId}-feld-33-11`}
                  render={({ field: zmField }) => (
                    <Input
                      {...zmField}
                      label={t('auftraege.33_10.lzbBk')}
                      variant="solid"
                      className="w-full"
                      data-testid={`${testId}-feld-33-11-inner`}
                      errors={fieldErrors}
                    />
                  )}
                />
              </AuftragOption>
              <AuftragOption
                value={'etcsBk'}
                label={t('auftraege.33_10.etcsBk_label')}
                data-testid={`${testId}-feld-33-11-12-selector-options-etcs-bk`}
              >
                <BefehlFormField
                  name={`${fieldPrefix}.feld_33_12`}
                  label={t('auftraege.33_10.etcsBk')}
                  control={control}
                  readOnly={readOnly}
                  activateEmptyWarning={activateEmptyFieldWarnings}
                  data-testid={`${testId}-feld-33-12`}
                  render={({ field: zmField }) => (
                    <Input
                      {...zmField}
                      label={t('auftraege.33_10.etcsBk')}
                      variant="solid"
                      className="w-full"
                      data-testid={`${testId}-feld-33-12-inner`}
                      errors={fieldErrors}
                    />
                  )}
                />
              </AuftragOption>
            </AuftragOptionen>
          )}
        />
      </div>
      <div className="flex flex-col flex-wrap items-baseline sm:flex-row w-full gap-0.5">
        <span data-testid={`${testId}-text-suffix`}>
          {t('auftraege.33_10.suffix')}
        </span>
        <BefehlFormField
          name="auftrag_33_10.lzb_etcs"
          control={control}
          label=""
          readOnly={false}
          disabled={!auftragActive}
          activateEmptyWarning={false}
          data-testid={`${testId}-text-lzb-etcs`}
          render={({ field }) => {
            const options: RadioChipOption[] = [
              {
                value: 'lzb_fahrt',
                label: t('auftraege.33_10.lzb_fahrt')
              },
              {
                value: 'etcs_fahrterlaubnis',
                label: t('auftraege.33_10.etcs_fahrterlaubnis')
              }
            ]
            return (
              <RadioChipGroup
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                options={options}
                disabled={true}
                readOnly={readOnly}
                variant="plain"
                showOnlySelected={readOnly}
                testId={`${testId}-text-lzb-etcs-inner`}
              />
            )
          }}
        />
        <span data-testid={`${testId}-text-anhalten`}>
          {t('auftraege.33_10.anhalten')}
        </span>
      </div>
    </BefehlFormAuftrag>
  )
}
